/* ============================================================
 * DeviceCoreService
 * ------------------------------------------------------------
 * Servicio para obtener información del dispositivo y gestionar
 * el token local de identificación del mismo.
 *
 * Mejoras:
 * - Compatibilidad con Ionic 7 + Capacitor 7
 * - Detecta IP y tipo de conexión (wifi, cellular, none)
 * - Obtiene versión de la app
 * - Guarda todo en Preferences para sincronización
 * ============================================================ */

import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { v4 as uuidv4 } from 'uuid';

export interface DeviceInfoModel {
  uuid: string;
  platform: string;
  model: string;
  osVersion: string;
  appVersion?: string;
  manufacturer?: string;
  deviceToken: string;
  networkType?: string;
  isConnected?: boolean;
  ip?: string;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceCoreService {
  private readonly TOKEN_KEY = 'device_token';
  private deviceInfo: DeviceInfoModel | null = null;

  constructor() {}

  /* ============================================================
   * 🔹 Inicializa el servicio y obtiene info del dispositivo
   * - Genera deviceToken si no existe
   * - Detecta tipo de red y dirección IP
   * ============================================================ */
  async init(): Promise<DeviceInfoModel> {

    if (this.deviceInfo) return this.deviceInfo;
  
    let uuid = '';
    let deviceInfo: any = {
      platform: 'web',
      model: 'browser',
      osVersion: '',
      manufacturer: 'browser'
    };
  
    let appVersion = 'web';
  
    try {
  
      // SOLO SI NO ES WEB
      if ((window as any).Capacitor?.isNativePlatform()) {
  
        const dInfo = await Device.getInfo();
        const dId = await Device.getId();
  
        uuid = dId.identifier;
        deviceInfo = dInfo;
  
        const appInfo = await App.getInfo();
        appVersion = appInfo.version;
  
      } else {
  
        // fallback para navegador
        uuid = 'WEB-' + Math.random().toString(36).substring(2);
  
      }
  
    } catch (err) {
      console.warn('[Device] Modo web detectado', err);
      uuid = 'WEB-' + Math.random().toString(36).substring(2);
    }
  
    // token del dispositivo
    let token = await this.getStoredDeviceToken();
  
    if (!token) {
      token = uuidv4();
      await Preferences.set({ key: this.TOKEN_KEY, value: token });
    }
  
    // red
    const networkStatus = await Network.getStatus();
  
    this.deviceInfo = {
      uuid,
      platform: deviceInfo.platform,
      model: deviceInfo.model || 'browser',
      osVersion: deviceInfo.osVersion || '',
      manufacturer: deviceInfo.manufacturer || '',
      appVersion,
      deviceToken: token,
      networkType: networkStatus.connectionType,
      isConnected: networkStatus.connected,
      timestamp: new Date().toISOString()
    };
  
    console.log('[Device] Información inicializada:', this.deviceInfo);
  
    return this.deviceInfo;
  }
  
  /* ============================================================
   * 🔹 Retorna el token único de este dispositivo
   * ============================================================ */
  async getDeviceToken(): Promise<string> {
    if (!this.deviceInfo) await this.init();
    return this.deviceInfo!.deviceToken;
  }

  /* ============================================================
   * 🔹 Retorna toda la información del dispositivo
   * ============================================================ */
  async getDeviceInfo(): Promise<DeviceInfoModel> {
    if (!this.deviceInfo) await this.init();
    return this.deviceInfo!;
  }

  /* ============================================================
   * 🔹 Limpia el token del dispositivo (logout o reseteo)
   * ============================================================ */
  async clearDeviceToken(): Promise<void> {
    await Preferences.remove({ key: this.TOKEN_KEY });
    this.deviceInfo = null;
    console.log('[Device] Token eliminado del almacenamiento local.');
  }

  /* ============================================================
   * 🔹 Obtiene el token almacenado en Preferences
   * ============================================================ */
  private async getStoredDeviceToken(): Promise<string | null> {
    const stored = await Preferences.get({ key: this.TOKEN_KEY });
    return stored.value ?? null;
  }
}
